import React from "react";
import { FiArchive, FiFile, FiImage } from "react-icons/fi";
import { deleteFile } from "helpers/fileHelper";
import { FileableFile, UploadFile } from "types/storage.types";
import { FileUploadType } from "./types/FileUpload.types";
import { AllowedFileType } from "./types/AllowedFileType.types";
import { MIME_MAP } from "./constants/acceptedFileTypes";
import { UploadedFile } from "./types/UploadedFile.type";
import { UploadStatus } from "./types/UploadStatus.type";
import { allIcons } from "assets/ImportedIcons";
import { safeRandomUUID } from "newui/utils/general";

export const deleteFileUpload = async (
	file: UploadFile,
	fileUploadType: FileUploadType
) => {
	try {
		await deleteFile(file.id, fileUploadType, file.file_path);

		return { success: true, file: file };
	} catch (error) {
		return { success: false, file: file };
	}
};

/**
 * Validates the uploaded file against name length and size restrictions
 * @param file
 * @param options
 * @returns
 */
export function validateFile(
	file: File,
	options: {
		maxNameLength?: number;
		maxSizeMB?: number;
	}
): string | null {
	if (options.maxSizeMB && file.size > options.maxSizeMB * 1024 * 1024) {
		return `Max file size is ${options.maxSizeMB}MB`;
	}

	if (options.maxNameLength && file.name.length > options.maxNameLength) {
		return `Filename too long`;
	}

	return null;
}

export const getFileMimeRecords = (types: AllowedFileType[]) => {
	const result = types.reduce<Record<string, string[]>>((acc, type) => {
		Object.assign(acc, MIME_MAP[type]);
		return acc;
	}, {});

	return result;
};

const getCleanFilenameFromS3Url = (url: string) => {
	try {
		if (!url) {
			return "";
		}

		const parsed = new URL(url);

		const disposition = parsed.searchParams.get("response-content-disposition");
		let filename = "";

		if (disposition) {
			const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/);
			if (match) {
				filename = decodeURIComponent(match[1]);
			}
		}

		if (!filename) {
			filename = parsed.pathname.split("/").pop() || "";
		}

		return filename.replace(/^[a-f0-9]{40}/i, "");
	} catch {
		return "";
	}
};

export const convertFileAbleFileToUploadedFile = (
	file: FileableFile
): UploadedFile => {
	if (!file) {
		return null;
	}

	const fileName = file.file_name ?? getCleanFilenameFromS3Url(file.file_path);

	return {
		id: file.id ?? 0,
		name: fileName,
		size: file.file_size,
		uploadId: file.id ? file.id.toString() : safeRandomUUID(),
		status: UploadStatus.UPLOADED,
		path: file.file_path,
		storage_path: file.storage_path,
	} as UploadedFile;
};

export const convertUploadedFileToFileAbleFile = (
	file: UploadedFile
): FileableFile => {
	if (!file) {
		return null;
	}

	return {
		id: file.id,
		file_name: file.name,
		file_size: file.size,
		file_path: file.path,
	} as FileableFile;
};

export const getFileTypeIcon = (filename: string): React.ReactElement => {
	const ext = filename.split(".").pop()?.toLowerCase();

	if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext || "")) {
		return React.createElement(FiImage, {
			size: 20,
			className: "text-blue-500",
		});
	}

	if (["zip", "rar", "7z", "tar", "gz"].includes(ext || "")) {
		return React.createElement(FiArchive, {
			size: 20,
			className: "text-yellow-600",
		});
	}

	if (ext in allIcons) {
		const IconComponent = allIcons[ext];
		// IconComponent is a React component
		return React.createElement(IconComponent, {
			height: 30,
			width: 30,
		});
	}

	return React.createElement(FiFile, {
		size: 20,
		className: "text-gray-500",
	});
};
